import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, IconButton, LinearProgress, MenuItem,
  Paper, Snackbar, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, Refresh as RefreshIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import billingService from '../../services/billingService';

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString());

/** topK values arrive as an array (or stringified array) — render a few. */
const topValues = (v) => {
  let arr = v;
  if (typeof v === 'string') { try { arr = JSON.parse(v); } catch { arr = [v]; } }
  if (!Array.isArray(arr)) return '';
  return arr.slice(0, 5).join(', ');
};

/**
 * Cardinality governance report (Phase 1 — measurement). Per-tenant distinct
 * metric-series counts vs plan budget, expandable into the worst metrics and
 * their worst label keys. Sourced from the metric_series / metric_dimensions
 * catalogs (HLL sketches), so it never scans the metrics firehose.
 */
const ClickHouseCardinalityPanel = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lookback, setLookback] = useState(7);
  const [openOrg, setOpenOrg] = useState(null);
  const [detail, setDetail] = useState({}); // orgId -> { loading, data }
  const [backfilling, setBackfilling] = useState(false);
  const [snack, setSnack] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await billingService.cardinalityOverview(lookback);
      setRows(Array.isArray(res.data) ? res.data : []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load cardinality overview');
    } finally {
      setLoading(false);
    }
  }, [lookback]);

  useEffect(() => { load(); }, [load]);

  const backfill = async () => {
    setBackfilling(true);
    try {
      const res = await billingService.cardinalityBackfill(Math.min(lookback, 30));
      setSnack(`Backfilled ${res.data?.backfilledDays ?? ''}d of series history`);
      await load();
    } catch (e) {
      setSnack(e?.response?.data?.error || 'Backfill failed');
    } finally { setBackfilling(false); }
  };

  const toggle = async (orgId) => {
    if (openOrg === orgId) { setOpenOrg(null); return; }
    setOpenOrg(orgId);
    if (!detail[orgId]) {
      setDetail((d) => ({ ...d, [orgId]: { loading: true } }));
      try {
        const res = await billingService.cardinalityReport(orgId, lookback);
        setDetail((d) => ({ ...d, [orgId]: { loading: false, data: res.data } }));
      } catch (e) {
        setDetail((d) => ({ ...d, [orgId]: { loading: false, error: e?.response?.data?.error || 'Failed to load report' } }));
      }
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>Cardinality governance</Typography>
        <TextField select size="small" label="Window" value={lookback}
                   onChange={(e) => setLookback(Number(e.target.value))} sx={{ width: 130 }}>
          {[1, 7, 14, 30, 90].map((d) => <MenuItem key={d} value={d}>Last {d}d</MenuItem>)}
        </TextField>
        <Tooltip title="Backfill the series catalog from historical metrics (the catalog otherwise only sees new inserts)">
          <span>
            <Button size="small" variant="outlined" startIcon={backfilling ? <CircularProgress size={14} /> : <HistoryIcon />}
                    onClick={backfill} disabled={backfilling}>Backfill</Button>
          </span>
        </Tooltip>
        <Tooltip title="Refresh"><span>
          <IconButton size="small" onClick={load} disabled={loading}><RefreshIcon fontSize="small" /></IconButton>
        </span></Tooltip>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Distinct metric <b>series</b> (label combinations) each tenant holds, versus its plan's
        <code> max_series</code> budget. Expand a tenant to see the worst metrics and the label keys
        driving cardinality. A budget of “—” means unlimited (set it on the plan). Enforcement (folding
        over-budget dimensions to <code>__overflow__</code>) arrives in Phase 2 — this is the measurement.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={24} /></Box>
      ) : rows.length === 0 ? (
        <Alert severity="info">No series data yet. The <code>metric_series</code> catalog populates from new
          metric inserts; check back once ingestion has run, or widen the window.</Alert>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Tenant</TableCell>
              <TableCell align="right">Distinct series</TableCell>
              <TableCell align="right">Budget</TableCell>
              <TableCell sx={{ width: 200 }}>Usage</TableCell>
              <TableCell align="right">Folded today</TableCell>
              <TableCell align="right">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => {
              const isOpen = openOrg === r.orgId;
              const d = detail[r.orgId];
              const pct = r.usagePct;
              return (
                <React.Fragment key={r.orgId}>
                  <TableRow hover sx={{ '& > *': { borderBottom: isOpen ? 'unset' : undefined } }}>
                    <TableCell>
                      <IconButton size="small" onClick={() => toggle(r.orgId)}>
                        {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.orgName || `org ${r.orgId}`}</Typography>
                      <Typography variant="caption" color="text.secondary">org {r.orgId}</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmt(r.totalSeries)}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{r.seriesBudget > 0 ? fmt(r.seriesBudget) : '—'}</TableCell>
                    <TableCell>
                      {pct == null ? (
                        <Typography variant="caption" color="text.secondary">unlimited</Typography>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress variant="determinate" value={Math.min(100, pct)}
                            color={pct >= 100 ? 'error' : pct >= 80 ? 'warning' : 'primary'}
                            sx={{ flex: 1, height: 6, borderRadius: 3 }} />
                          <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>{pct}%</Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {(() => {
                        const f = r.foldsToday || {};
                        const tot = (Number(f.foldedValues) || 0) + (Number(f.foldedSeries) || 0);
                        return tot > 0
                          ? <Tooltip title={`${fmt(f.foldedValues)} value folds, ${fmt(f.foldedSeries)} series folds`}>
                              <Chip size="small" color="warning" label={fmt(tot)} />
                            </Tooltip>
                          : <Typography variant="caption" color="text.secondary">—</Typography>;
                      })()}
                    </TableCell>
                    <TableCell align="right">
                      {r.overBudget
                        ? <Chip size="small" color="error" label="Over budget" />
                        : <Chip size="small" color="success" variant="outlined" label="OK" />}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 0, border: 0 }}>
                      <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2 }}>
                          {!d || d.loading ? (
                            <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={20} /></Box>
                          ) : d.error ? (
                            <Alert severity="warning">{d.error}</Alert>
                          ) : (d.data?.metrics || []).length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              {d.data?.error || 'No per-metric series data in this window.'}
                            </Typography>
                          ) : (
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Metric</TableCell>
                                  <TableCell align="right">Series</TableCell>
                                  <TableCell>Worst label keys (distinct values)</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {d.data.metrics.map((m) => (
                                  <TableRow key={m.metric}>
                                    <TableCell><code>{m.metric}</code></TableCell>
                                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmt(m.series)}</TableCell>
                                    <TableCell>
                                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                        {(m.topKeys || []).map((k) => (
                                          <Tooltip key={k.key} title={topValues(k.topValues)}>
                                            <Chip size="small" variant={k.overCeiling ? 'filled' : 'outlined'}
                                              color={k.overCeiling ? 'error' : 'default'}
                                              label={`${k.key}: ${fmt(k.cardinality)}`}
                                              sx={{ fontSize: 11, height: 20 }} />
                                          </Tooltip>
                                        ))}
                                      </Stack>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      )}
      <Snackbar open={!!snack} autoHideDuration={5000} onClose={() => setSnack(null)} message={snack} />
    </Paper>
  );
};

export default ClickHouseCardinalityPanel;
